import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { resetUserPassword, verifyResetPasswordCode } from '../api/users';
import EmptyPoolIcon from '../assets/icons/empty-pool.svg';
import EyeOff from '../assets/icons/eye-off.svg';
import EyeOn from '../assets/icons/eye-on.svg';
import SomethingWrongPoolIcon from '../assets/icons/something-wrong-pool.svg';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton';
import AppFormField from '../components/common/AppFormField';

const validationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password should have at least 8 characters')
    .max(18, 'Password can have at most 18 characters')
    .matches(/[A-Z]/, 'Password must contain at least 1 upper case letter')
    .matches(/[a-z]/, 'Password must contain at least 1 lower case letter')
    .matches(
      /[!@#$%^&*()_\-+=<>?]/,
      'Password must contain at least 1 special character'
    )
    .matches(/\d+/, 'Password must contain at least 1 number')
    .label('Password'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .label('Confirm password'),
});

type PasswordResetFormValues = Yup.InferType<typeof validationSchema>;

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  // Page-level error for invalid/expired links, etc.
  const [pageError, setPageError] = useState<string>('');
  // Form-level error for password reset API failures
  const [formError, setFormError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const methods = useForm<PasswordResetFormValues>({
    defaultValues: { password: '', confirmPassword: '' },
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    const mode = params.mode;
    const code = params.oobCode;

    const INVALID_LINK_MESSAGE =
      'Please request a new password link by pressing "Re-send email" in the app.';

    if (!mode || mode !== 'resetPassword' || !code) {
      setPageError(INVALID_LINK_MESSAGE);
      setLoading(false);
      return;
    }

    setOobCode(code);

    verifyResetPasswordCode(code)
      .then((result) => {
        if (result?.error) {
          setPageError(INVALID_LINK_MESSAGE);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to verify reset code:', err);
        setPageError(INVALID_LINK_MESSAGE);
        setLoading(false);
      });
  }, [searchParams]);

  const handleSubmit = async (data: PasswordResetFormValues) => {
    setFormError('');

    if (!oobCode) {
      setPageError(
        'Please request a new password link by pressing "Re-send email" in the app.'
      );
      return;
    }

    try {
      setSubmitting(true);
      const result = await resetUserPassword(oobCode, data.password);

      if (result?.error) {
        setFormError(result.error);
      } else if (result?.data?.status === 'success') {
        setSuccess(true);
      } else {
        setFormError('Unexpected response. Please try again.');
      }
    } catch (error) {
      console.error('Password reset failed:', error);
      setFormError('Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <EmptyPoolIcon />
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold">
              Checking password reset request
            </h2>
            <p>Please wait a moment...</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Error state for invalid/expired reset links
  if (pageError) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <SomethingWrongPoolIcon />
          <div className="flex flex-col gap-4">
            <h2 className="text-2xl font-bold text-dark">
              Invalid reset request
            </h2>
            <p>{pageError}</p>
          </div>
          <div className="w-full px-8">
            <AppButton
              fullWidth
              variant="primary"
              onClick={() => (window.location.href = '/')}
            >
              Go home
            </AppButton>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center text-center gap-8">
          <h2 className="text-2xl font-bold text-dark">
            Password reset successfully
          </h2>
          <div className="flex flex-col gap-4">
            <p>Your password has been successfully changed.</p>
            <p>Please log in using your new password.</p>
          </div>
          <div className="w-full px-8">
            <AppButton
              fullWidth
              variant="primary"
              onClick={() => (window.location.href = '/login')}
            >
              Go To Log In
            </AppButton>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="max-w-md mx-auto">
        <div className="flex flex-col items-center text-center gap-8 mb-8">
          <h2 className="text-2xl font-bold ">Set a new password</h2>
          <p>Enter and confirm your new password to complete the reset.</p>
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="flex flex-col gap-8"
          >
            <div className="flex flex-col gap-4">
              {formError && (
                <p className="bg-dangerLight border border-danger/20 text-danger text-sm p-3 rounded-lg mb-4">
                  {formError}
                </p>
              )}

              <AppFormField<PasswordResetFormValues>
                name="password"
                label="New password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-light hover:text-dark"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? <EyeOff /> : <EyeOn />}
                  </button>
                }
              />

              <AppFormField<PasswordResetFormValues>
                name="confirmPassword"
                label="Confirm new password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-light hover:text-dark"
                    aria-label={
                      showConfirmPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showConfirmPassword ? <EyeOff /> : <EyeOn />}
                  </button>
                }
              />
            </div>

            <div className="flex flex-col gap-8 px-8">
              <AppButton type="submit" fullWidth loading={submitting}>
                Set New Password
              </AppButton>

              <AppButton
                fullWidth
                variant="tertiary"
                onClick={() => (window.location.href = '/login')}
              >
                <ArrowLeft />
                BACK TO LOGIN
              </AppButton>
            </div>
          </form>
        </FormProvider>
      </div>
    </AuthLayout>
  );
};

export default PasswordResetPage;
